import { useCallback, useState } from "react";
import { IFavoriteOriginWithMonths } from "../../../../types/IFavoriteOrigins";
import { DragState, ProductSectionOrder, SectionKey } from "../../../../types/IOriginManager";
import {
  applySectionOrderNumbers,
  getActiveOrigins,
  getDefaultProductSectionOrder,
  getRowKey,
  isValidDragTarget,
  reorderSectionRowKeys,
  updateProductSectionOrder,
} from "../FavoriteOriginHelper";
import { buildOrderedFavLocMaps, reorderFavorites } from "../originManagerCrudActions";

interface UseOriginManagerSectionOrderParams {
  selectedProduct: string;
  getMarketerIdentifier: () => string;
  refreshFavorites: (marketer: string) => Promise<unknown[]>;
}

export interface UseOriginManagerSectionOrderResult {
  sectionOrderByProduct: Record<string, ProductSectionOrder>;
  setSectionOrderByProduct: React.Dispatch<
    React.SetStateAction<Record<string, ProductSectionOrder>>
  >;
  setPendingOrderSaveByProduct: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  dragState: DragState | null;
  dragOverRowKey: string | null;
  handleDragStart: (
    event: React.DragEvent<HTMLButtonElement>,
    rowKey: string,
    section: SectionKey,
  ) => void;
  handleRowDragOver: (
    event: React.DragEvent<HTMLDivElement>,
    rowKey: string,
    section: SectionKey,
  ) => void;
  handleRowDrop: (
    event: React.DragEvent<HTMLDivElement>,
    rowKey: string,
    section: SectionKey,
    rows: IFavoriteOriginWithMonths[],
  ) => void;
  handleDragEnd: () => void;
  savePendingOrderInBackground: (
    productKey: string,
    savedOrigins: IFavoriteOriginWithMonths[],
    newlyAddedOrigins: Record<string, IFavoriteOriginWithMonths[]>,
    deletedRowKeysByProduct: Record<string, Set<string>>,
  ) => void;
}

export const useOriginManagerSectionOrder = ({
  selectedProduct,
  getMarketerIdentifier,
  refreshFavorites,
}: UseOriginManagerSectionOrderParams): UseOriginManagerSectionOrderResult => {
  const [sectionOrderByProduct, setSectionOrderByProduct] = useState<
    Record<string, ProductSectionOrder>
  >({});
  const [pendingOrderSaveByProduct, setPendingOrderSaveByProduct] = useState<
    Record<string, boolean>
  >({});
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [dragOverRowKey, setDragOverRowKey] = useState<string | null>(null);

  const handleDragStart = useCallback(
    (
      event: React.DragEvent<HTMLButtonElement>,
      rowKey: string,
      section: SectionKey,
    ) => {
      const { dataTransfer } = event;
      dataTransfer.effectAllowed = "move";
      dataTransfer.setData("text/plain", rowKey);
      setDragState({ rowKey, section });
      setDragOverRowKey(null);
    },
    [],
  );

  const handleRowDragOver = useCallback(
    (
      event: React.DragEvent<HTMLDivElement>,
      rowKey: string,
      section: SectionKey,
    ) => {
      if (!isValidDragTarget(dragState, rowKey, section)) {
        return;
      }

      event.preventDefault();
      const { dataTransfer } = event;
      dataTransfer.dropEffect = "move";
      setDragOverRowKey(rowKey);
    },
    [dragState],
  );

  const handleRowDrop = useCallback(
    (
      event: React.DragEvent<HTMLDivElement>,
      rowKey: string,
      section: SectionKey,
      rows: IFavoriteOriginWithMonths[],
    ) => {
      event.preventDefault();

      if (!dragState || !isValidDragTarget(dragState, rowKey, section)) {
        setDragState(null);
        setDragOverRowKey(null);
        return;
      }

      const sectionRows = rows.filter((origin) =>
        section === "koch" ? !origin.competitor : origin.competitor,
      );
      const currentOrder = sectionRows.map(getRowKey);

      const targetRect = (
        event.currentTarget as HTMLDivElement
      ).getBoundingClientRect();
      const placeAfter = event.clientY > targetRect.top + targetRect.height / 2;
      const nextOrder = reorderSectionRowKeys(
        currentOrder,
        dragState.rowKey,
        rowKey,
        placeAfter,
      );

      if (!nextOrder || nextOrder.join("|") === currentOrder.join("|")) {
        setDragState(null);
        setDragOverRowKey(null);
        return;
      }

      const prevOrder =
        sectionOrderByProduct[selectedProduct] ?? getDefaultProductSectionOrder();
      const newProductOrder = updateProductSectionOrder(
        prevOrder,
        section,
        nextOrder,
      );

      setSectionOrderByProduct((prev) => ({
        ...prev,
        [selectedProduct]: newProductOrder,
      }));

      setPendingOrderSaveByProduct((prev) => ({
        ...prev,
        [selectedProduct]: true,
      }));

      setDragState(null);
      setDragOverRowKey(null);
    },
    [dragState, sectionOrderByProduct, selectedProduct],
  );

  const handleDragEnd = useCallback(() => {
    setDragState(null);
    setDragOverRowKey(null);
  }, []);

  const savePendingOrderInBackground = useCallback(
    (
      productKey: string,
      savedOrigins: IFavoriteOriginWithMonths[],
      newlyAddedOrigins: Record<string, IFavoriteOriginWithMonths[]>,
      deletedRowKeysByProduct: Record<string, Set<string>>,
    ) => {
      if (!pendingOrderSaveByProduct[productKey]) {
        return;
      }

      const marketer = getMarketerIdentifier();
      if (!marketer) {
        return;
      }

      const currentSectionOrder =
        sectionOrderByProduct[productKey] ?? getDefaultProductSectionOrder();
      const deletedRowKeys =
        deletedRowKeysByProduct[productKey] ?? new Set<string>();
      const favorites = getActiveOrigins(
        savedOrigins,
        newlyAddedOrigins[productKey] ?? [],
        deletedRowKeys,
      );
      const favoritesWithOrder = applySectionOrderNumbers(
        favorites,
        currentSectionOrder,
      );

      const orderedFavLocMaps = buildOrderedFavLocMaps(
        productKey,
        favoritesWithOrder,
        new Map<string, string>(),
        getRowKey,
      );

      if (orderedFavLocMaps.length === 0) {
        return;
      }

      setPendingOrderSaveByProduct((prev) => ({
        ...prev,
        [productKey]: false,
      }));

      reorderFavorites(marketer, productKey, favoritesWithOrder, getRowKey)
        .then(() => refreshFavorites(marketer))
        .catch(() => {
          setPendingOrderSaveByProduct((prev) => ({
            ...prev,
            [productKey]: true,
          }));
        });
    },
    [
      getMarketerIdentifier,
      pendingOrderSaveByProduct,
      refreshFavorites,
      sectionOrderByProduct,
    ],
  );

  return {
    sectionOrderByProduct,
    setSectionOrderByProduct,
    setPendingOrderSaveByProduct,
    dragState,
    dragOverRowKey,
    handleDragStart,
    handleRowDragOver,
    handleRowDrop,
    handleDragEnd,
    savePendingOrderInBackground,
  };
};
